import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { checkAcceptedFileTypes, validateFileSize } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { infoSx, labelSx, multilineSx, radioGroupSx, uploadContainerSx } from './eligibilityStyles';
import { DocumentLine } from 'containers/tunnel/common';
import {
    ErrorMessage,
    FileErrorMessage,
    FileInputButton,
    InfoButton,
    RadioLabel,
} from 'components';
import { Box, Typography, FormControl, RadioGroup, FormLabel } from '@mui/material';

const EligibilityFCPR = props => {
    const {
        messages,
        investorQualification,
        investorProData,
        formError,
        setFormError,
        setInvestorProData,
        children,
        tunnelSubscription,
    } = props;

    const [fileSizeError, setFileSizeError] = useState({
        transactionsCompletedDocuments: false,
        portfolioValueDocuments: false,
        professionalPositionHeldDocuments: false,
    });
    const [fileTypeError, setFileTypeError] = useState({
        transactionsCompletedDocuments: false,
        portfolioValueDocuments: false,
        professionalPositionHeldDocuments: false,
    });

    const handleRadioChange = useCallback(
        e => {
            const { name, value } = e.target;
            setInvestorProData(prevState => ({ ...prevState, [name]: value }));
            setFormError('');
        },
        [setInvestorProData, setFormError]
    );

    const handleFileChange = useCallback(
        e => {
            const name = e.target.name;
            const file = e.target.files[0];
            checkAcceptedFileTypes(
                file,
                () => {
                    setFileTypeError(prevState => ({ ...prevState, [name]: false }));
                    if (validateFileSize(file)) {
                        setFileSizeError(prevState => ({ ...prevState, [name]: false }));
                        setInvestorProData(prevState => ({
                            ...prevState,
                            [name]: [...prevState[name], file],
                        }));
                    } else {
                        setFileSizeError(prevState => ({ ...prevState, [name]: true }));
                    }
                },
                () => {
                    setFileTypeError(prevState => ({ ...prevState, [name]: true }));
                    setFileSizeError(prevState => ({ ...prevState, [name]: false }));
                }
            );
        },
        [setInvestorProData]
    );

    const handleRemoveFile = useCallback(
        (key, name) => () => {
            setInvestorProData(prevState => ({
                ...prevState,
                [key]: prevState[key].filter(f => f.name !== name),
            }));
        },
        [setInvestorProData]
    );

    return (
        <React.Fragment>
            <Box sx={multilineSx}>
                <Box display="flex" alignItems="baseline" justifyContent="space-between">
                    <RadioLabel
                        sx={labelSx}
                        label={messages['step34_fcpr_pp_real.question_1']}
                        value="step34_fcpr_pp_real.question_1"
                    />
                </Box>
            </Box>
            <Box sx={multilineSx}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <RadioLabel
                        sx={labelSx}
                        label={messages['step34_fcpr_pp_real.question_2']}
                        value="step34_fcpr_pp_real.question_2"
                    />
                    <InfoButton
                        disableMarginBottom
                        title={texts.ELIGIBILITEE_Q2_INFO_TITLE}
                        content={texts.SURVEY_RESULTS_INFO_BUTTON_CONTENT(
                            tunnelSubscription?.investmentFund?.promoter?.name
                        )}
                    />
                </Box>
                {investorQualification?.includes('step34_fcpr_pp_real.question_2') && (
                    <Box display="flex" flexDirection="column" alignItems="flex-start" ml={5}>
                        <Typography gutterBottom sx={infoSx}>
                            {texts.SURVEY_RESULTS_SELECT_HEADER}
                        </Typography>
                        <Typography gutterBottom sx={infoSx}>
                            {texts.SURVEY_RESULTS_SELECT_OPTIONS}
                        </Typography>
                        <FormControl>
                            <FormLabel sx={labelSx}>
                                {texts.SURVEY_RESULTS_HAS_TRANSACTIONS}
                            </FormLabel>
                            <RadioGroup
                                sx={radioGroupSx}
                                name="transactionsCompleted"
                                value={investorProData.transactionsCompleted}
                                onChange={handleRadioChange}
                            >
                                <RadioLabel label="Oui" value="true" />
                                <RadioLabel label="Non" value="false" />
                            </RadioGroup>
                            {investorProData.transactionsCompleted === 'true' && (
                                <Box sx={uploadContainerSx}>
                                    <Typography gutterBottom>
                                        {texts.SURVEY_RESULTS_HAS_TRANSACTIONS_DOCS}
                                    </Typography>
                                    <FileInputButton
                                        noMargin
                                        showIcon
                                        label={texts.UPLOAD_FILE_LABEL}
                                        id="transactionsCompletedDocuments"
                                        onFileChange={handleFileChange}
                                    />
                                    <Box sx={{ mt: '10px' }}>
                                        {investorProData.transactionsCompletedDocuments.map(doc => (
                                            <DocumentLine
                                                key={doc.name}
                                                doc={doc}
                                                onDelete={handleRemoveFile(
                                                    'transactionsCompletedDocuments',
                                                    doc.name
                                                )}
                                            />
                                        ))}
                                    </Box>
                                    {fileSizeError.transactionsCompletedDocuments && (
                                        <FileErrorMessage variant="size" useMargin={false} />
                                    )}
                                    {fileTypeError.transactionsCompletedDocuments && (
                                        <FileErrorMessage variant="type" useMargin={false} />
                                    )}
                                </Box>
                            )}
                        </FormControl>
                        <FormControl>
                            <FormLabel sx={labelSx}>
                                {texts.SURVEY_RESULTS_PORTFOLIO_VALUE}
                            </FormLabel>
                            <RadioGroup
                                sx={radioGroupSx}
                                name="portfolioValue"
                                value={investorProData.portfolioValue}
                                onChange={handleRadioChange}
                            >
                                <RadioLabel label="Oui" value="true" />
                                <RadioLabel label="Non" value="false" />
                            </RadioGroup>
                            {investorProData.portfolioValue === 'true' && (
                                <Box sx={uploadContainerSx}>
                                    <Typography gutterBottom>
                                        {texts.SURVEY_RESULTS_PORTFOLIO_VALUE_DOCS}
                                    </Typography>
                                    <FileInputButton
                                        noMargin
                                        showIcon
                                        label={texts.UPLOAD_FILE_LABEL}
                                        id="portfolioValueDocuments"
                                        onFileChange={handleFileChange}
                                    />
                                    <Box sx={{ mt: '10px' }}>
                                        {investorProData.portfolioValueDocuments.map(doc => (
                                            <DocumentLine
                                                key={doc.name}
                                                doc={doc}
                                                onDelete={handleRemoveFile(
                                                    'portfolioValueDocuments',
                                                    doc.name
                                                )}
                                            />
                                        ))}
                                    </Box>
                                    {fileSizeError.portfolioValueDocuments && (
                                        <FileErrorMessage variant="size" useMargin={false} />
                                    )}
                                    {fileTypeError.portfolioValueDocuments && (
                                        <FileErrorMessage variant="type" useMargin={false} />
                                    )}
                                </Box>
                            )}
                        </FormControl>
                        <FormControl>
                            <FormLabel sx={labelSx}>
                                {texts.SURVEY_RESULTS_PROFESSIONAL_POSITION_HELD}
                            </FormLabel>
                            <RadioGroup
                                sx={radioGroupSx}
                                name="professionalPositionHeld"
                                value={investorProData.professionalPositionHeld}
                                onChange={handleRadioChange}
                            >
                                <RadioLabel value="true" label="Oui" />
                                <RadioLabel value="false" label="Non" />
                            </RadioGroup>
                            {investorProData.professionalPositionHeld === 'true' && (
                                <Box sx={uploadContainerSx}>
                                    <Typography gutterBottom>
                                        {texts.SURVEY_RESULTS_PROFESSIONAL_POSITION_HELD_DOCS}
                                    </Typography>
                                    <FileInputButton
                                        noMargin
                                        showIcon
                                        onFileChange={handleFileChange}
                                        id="professionalPositionHeldDocuments"
                                        label={texts.UPLOAD_FILE_LABEL}
                                    />
                                    <Box sx={{ mt: '10px' }}>
                                        {investorProData.professionalPositionHeldDocuments.map(
                                            doc => (
                                                <DocumentLine
                                                    key={doc.name}
                                                    doc={doc}
                                                    onDelete={handleRemoveFile(
                                                        'professionalPositionHeldDocuments',
                                                        doc.name
                                                    )}
                                                />
                                            )
                                        )}
                                    </Box>
                                    {fileSizeError.professionalPositionHeldDocuments && (
                                        <FileErrorMessage variant="size" useMargin={false} />
                                    )}
                                    {fileTypeError.professionalPositionHeldDocuments && (
                                        <FileErrorMessage variant="type" useMargin={false} />
                                    )}
                                </Box>
                            )}
                        </FormControl>
                        {formError !== '' && <ErrorMessage message={formError} />}
                    </Box>
                )}
            </Box>
            {children}
            <Box sx={multilineSx}>
                <Box display="flex" alignItems="baseline" justifyContent="space-between">
                    <RadioLabel
                        label={messages['step34_fcpr_pp_real.question_3']}
                        sx={labelSx}
                        value="step34_fcpr_pp_real.question_3"
                    />
                </Box>
            </Box>
        </React.Fragment>
    );
};

EligibilityFCPR.propTypes = {
    messages: PropTypes.object,
    investorQualification: PropTypes.string,
    investorProData: PropTypes.object,
    formError: PropTypes.string,
    setFormError: PropTypes.func,
    setInvestorProData: PropTypes.func,
    children: PropTypes.node,
};

export default EligibilityFCPR;
